import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import type { TextFieldProps } from '@mui/material/TextField';
import TextField from '@mui/material/TextField';
import is from '@sindresorhus/is';
import type {
    ChangeEvent,
    FunctionComponent,
    HTMLAttributes,
    KeyboardEvent,
    MouseEventHandler,
    ReactElement,
} from 'react';
import { useContext, useState } from 'react';
import Highlighter from 'react-highlight-words';

import { ApplicationContext } from '~/shared/context/context';
import type { SkillShort } from '~/shared/data-contract/skill';

import { useExcludeSkills, useSearchSkillsQueryWithLimit } from './common';

const skillLabel = (skill: SkillShort | string): string => (typeof skill === 'string' ? skill : skill.title);

type SkillSearchInputProps = {
    readonly keyDownListener: (e: KeyboardEvent<HTMLInputElement>) => void;
    readonly onClick: MouseEventHandler;
    readonly onSearchTermChanged: (() => void) | undefined;
    readonly searchTerm: string;
};

type SkillSearchProps = {
    readonly excludedSkillIds: readonly string[];
    readonly onAddSkill?: ((skill: SkillShort) => void) | undefined;
    readonly onSearchTermChanged?: SkillSearchInputProps['onSearchTermChanged'];
    readonly onShowLoadMoreClicked?: (() => void) | undefined;
};

const defaultSkillSearchLimit = 10;

export const SkillSearch: FunctionComponent<SkillSearchProps> = (props) => {
    const [searchTerm, setSearchTerm] = useState('');
    const skillSearchQuery = useSearchSkillsQueryWithLimit(searchTerm, defaultSkillSearchLimit);
    const { filteredSkills } = useExcludeSkills(skillSearchQuery.skills, props.excludedSkillIds);
    const [dropdownIsOpen, setDropdownIsOpen] = useState(false);

    const { translate } = useContext(ApplicationContext);

    const onOpenDropdown = (): void => setDropdownIsOpen(true);
    const onCloseDropdown = (): void => setDropdownIsOpen(false);

    const onResetSearch = (): void => setSearchTerm('');

    const handleSkillAdd = (skill: SkillShort | string | null): void => {
        if (is.null(skill)) {
            return;
        }

        if (typeof skill === 'string') {
            return;
        }

        props.onAddSkill?.(skill);
        onResetSearch();
        onCloseDropdown();
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const searchValue = e.target.value;
        if (searchTerm === searchValue) {
            return;
        }
        setSearchTerm(searchValue);
        onOpenDropdown();
        props.onSearchTermChanged?.();
    };

    return (
        <ClickAwayListener onClickAway={onCloseDropdown}>
            <Autocomplete
                autoHighlight={true}
                filterOptions={(options) => options}
                getOptionLabel={skillLabel}
                id="skill-search-autocomplete"
                inputValue={searchTerm}
                open={dropdownIsOpen && is.nonEmptyArray(filteredSkills)}
                options={filteredSkills}
                renderInput={(params) => (
                    <TextField
                        {...(params as TextFieldProps)}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: is.nonEmptyString(searchTerm) ? (
                                <CloseIcon color="action" cursor="pointer" onClick={(_) => onResetSearch()} />
                            ) : null,
                        }}
                        label={translate('components.skills.search-placeholder')}
                        name="skill-search-input"
                        variant="outlined"
                        onChange={handleChange}
                        onFocus={onOpenDropdown}
                    />
                )}
                // biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration
                renderOption={(props: HTMLAttributes<HTMLLIElement>, option: SkillShort): ReactElement<any> => (
                    <li key={option.id} {...props}>
                        <Highlighter
                            autoEscape={true}
                            highlightTag="strong"
                            searchWords={[searchTerm]}
                            textToHighlight={option.title}
                        />
                    </li>
                )}
                onChange={(_event, value) => handleSkillAdd(value)}
            />
        </ClickAwayListener>
    );
};
